/**
 * ProductSegmentBar — presets + custom chips + quick-save + rule builder.
 *
 * A11y structure (filter pills, NOT tabs):
 * - These chips are filter pills with NO associated role="tabpanel" and no arrow-key roving
 *   tabindex, so role="tablist"/"tab" would be a false affordance (worse for SR than no role).
 *   Correct pattern = role="group" + toggle buttons with aria-pressed (see SegmentChip).
 * - <div role="group" aria-label> wraps the SegmentChip toggle buttons.
 * - Custom chips get NO `onManage`; each custom segment's <SegmentManageMenu> is rendered as a
 *   SIBLING after the group, owning its own DropdownMenuTrigger (icon button,
 *   data-testid="segment-chip-manage") which is the Radix anchor so the dropdown positions at
 *   the chip — not the viewport center.
 */
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProductSegments } from '@/hooks/useProductSegments';
import { PRESETS, presetFilters, ruleToFilters, filtersToRule, DEFAULT_SEGMENT_COLOR } from './segmentPresets';
import type { ProductSegmentRule } from './segmentPresets';
import type { ProductTableFilters } from '@/hooks/useProductTableConfig';
import { SegmentChip } from './SegmentChip';
import { SegmentQuickSave } from './SegmentQuickSave';
import { SegmentRuleBuilder } from './SegmentRuleBuilder';
import { SegmentManageMenu } from './SegmentManageMenu';
import { Button } from '@/components/v2/ui/Button';

interface Props {
  onApplyFilters: (filters: ProductTableFilters) => void;
  currentFilters: ProductTableFilters;
  countFn: (filters: ProductTableFilters) => Promise<number>;
}

export function ProductSegmentBar({ onApplyFilters, currentFilters, countFn }: Props) {
  const { t } = useTranslation();
  const { selectedId, customSegments, select, addSegment, renameSegment, recolorSegment, setPinned, updateRule, deleteSegment } =
    useProductSegments();
  const [builderOpen, setBuilderOpen] = useState(false);
  // Track which custom segment's manage menu is open (by id), or null.
  const [managingId, setManagingId] = useState<string | null>(null);

  // Stable wrapper for countFn: prevents SegmentRuleBuilder live-count debounce resets on each render.
  const stableCount = useCallback((f: ProductTableFilters) => countFn(f), [countFn]);

  const sorted = [...customSegments].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));

  function handleBuilderSave(name: string, rule: ProductSegmentRule) {
    addSegment({ name, color: DEFAULT_SEGMENT_COLOR, rule });
    setBuilderOpen(false);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* role="group" of toggle-button chips (aria-pressed). Custom chips get NO onManage; their
          manage menu is a SIBLING after this div (see file-level a11y comment). */}
      <div role="group" aria-label={t('productSegments.tablistLabel')} className="flex flex-wrap gap-1">
        {PRESETS.map((p) => (
          <SegmentChip key={p.id} label={t(p.labelKey)} active={selectedId === p.id}
            disabled={p.disabled}
            onSelect={() => { if (!p.disabled) { select(p.id); onApplyFilters(presetFilters(p.id)); } }}
          />
        ))}
        {sorted.map((seg) => (
          <SegmentChip key={seg.id} label={seg.name} active={selectedId === seg.id}
            color={seg.color}
            onSelect={() => { select(seg.id); onApplyFilters(ruleToFilters(seg.rule)); }}
          />
        ))}
      </div>

      {/* Manage menus — OUTSIDE the tablist; each owns its own DropdownMenuTrigger (anchor). */}
      {sorted.map((seg) => (
        <SegmentManageMenu key={seg.id} segment={seg}
          open={managingId === seg.id}
          onOpenChange={(o) => setManagingId(o ? seg.id : null)}
          countFn={stableCount}
          onRename={renameSegment} onRecolor={recolorSegment}
          onSetPinned={setPinned} onUpdateRule={updateRule} onDelete={deleteSegment}
        />
      ))}

      <SegmentQuickSave currentFilters={currentFilters}
        onSave={(name, filters) => addSegment({ name, color: DEFAULT_SEGMENT_COLOR, rule: filtersToRule(filters) })}
      />

      <Button variant="outline" size="sm" onClick={() => setBuilderOpen(true)}>
        {t('productSegments.advanced')}
      </Button>

      <SegmentRuleBuilder open={builderOpen} onClose={() => setBuilderOpen(false)}
        onSave={handleBuilderSave} countFn={stableCount} />
    </div>
  );
}
